import { Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Constants from "expo-constants";

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Interface for performance and telemetry metrics logged to Google Analytics.
 */
export interface IPerformanceMetrics {
  memoryUsageBytes?: number;
  memoryTotalBytes?: number;
  memoryHeapRatio?: number;
  networkEffectiveType?: string;
  networkDownlinkMbps?: number;
  networkRttMs?: number;
  apiLatencyMs?: number;
  apiFunction?: string;
  cpuLagMs?: number;
  longTaskCount?: number;
}

/**
 * Interface for general analytics event payloads.
 */
export interface IAnalyticsEventPayload {
  [key: string]: string | number | boolean | undefined | null;
}

const DEFAULT_MEASUREMENT_ID = "G-xxx";
const CLIENT_ID_STORAGE_KEY = "memories_ga_client_id";
export const ANALYTICS_CONSENT_STORAGE_KEY = "memories_analytics_consent";

let isInitialized = false;
let activeUserId: string | null = null;
let activeUserProps: Record<string, string | number | boolean> = {};
let cachedClientId: string | null = null;
let activeScreenName: string = "root";
let cachedAnalyticsConsent: boolean | null = null;

/**
 * Resets the in-memory analytics consent and initialization state for testing purposes.
 */
export function _resetAnalyticsStateForTesting(): void {
  cachedAnalyticsConsent = null;
  isInitialized = false;
  activeUserId = null;
  activeUserProps = {};
  cachedClientId = null;
}

/**
 * Checks whether the user has explicitly consented to analytical telemetry.
 * Defaults to false (unconsented) in strict compliance with ePrivacy Directive and GDPR requirements.
 * @returns {Promise<boolean>} True if analytical telemetry has been explicitly accepted by the user.
 */
export async function hasUserConsentedToAnalytics(): Promise<boolean> {
  if (cachedAnalyticsConsent !== null) {
    return cachedAnalyticsConsent;
  }

  try {
    const stored = await AsyncStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY);
    cachedAnalyticsConsent = stored === "true";
  } catch (err) {
    console.warn("[Analytics] Could not access AsyncStorage for consent status:", err);
    cachedAnalyticsConsent = false;
  }

  return cachedAnalyticsConsent;
}

/**
 * Synchronously checks cached consent status. Returns false if unconsented or unknown.
 * @returns {boolean} True if consent is explicitly known to be granted.
 */
export function getAnalyticsConsentSync(): boolean {
  return cachedAnalyticsConsent === true;
}

/**
 * Sets user analytics consent state, updates persistent storage, and activates or revokes GA4 collection.
 * @param {boolean} granted - Whether the user explicitly accepts analytical telemetry.
 */
export async function setAnalyticsConsent(granted: boolean): Promise<void> {
  cachedAnalyticsConsent = granted;
  try {
    await AsyncStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, granted ? "true" : "false");
  } catch (err) {
    console.warn("[Analytics] Could not persist analytics consent to AsyncStorage:", err);
  }

  const measurementId = getMeasurementId();

  if (granted) {
    // If granted, ensure window disable flag is cleared (if web) and initialize analytics
    if (Platform.OS === "web" && typeof window !== "undefined") {
      (window as any)[`ga-disable-${measurementId}`] = false;
    }
    await initAnalytics();
  } else {
    // Revoke consent: immediately disable GA on web and reset in-memory identity
    if (Platform.OS === "web" && typeof window !== "undefined") {
      (window as any)[`ga-disable-${measurementId}`] = true;
    }
    isInitialized = false;
    activeUserId = null;
    activeUserProps = {};
  }
}

/**
 * Retrieves or creates a stable client UUID for telemetry identification across native sessions.
 * @returns {Promise<string>} Persistent client ID.
 */
async function getOrCreateClientId(): Promise<string> {
  if (cachedClientId !== null) {
    return cachedClientId;
  }

  try {
    const stored = await AsyncStorage.getItem(CLIENT_ID_STORAGE_KEY);
    if (stored && stored.trim().length > 0) {
      cachedClientId = stored;
      return cachedClientId;
    }
  } catch (err) {
    console.warn("[Analytics] Could not access AsyncStorage for client_id:", err);
  }

  // Generate pseudo-UUID
  const newId = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

  cachedClientId = newId;
  try {
    await AsyncStorage.setItem(CLIENT_ID_STORAGE_KEY, newId);
  } catch (err) {
    console.warn("[Analytics] Could not persist client_id to AsyncStorage:", err);
  }

  return cachedClientId;
}

/**
 * Resolves the Google Analytics Measurement ID configured in environment variables.
 * @returns {string} The Measurement ID (e.g. G-XHH72XRQ69).
 */
export function getMeasurementId(): string {
  const vars = Constants.expoConfig?.extra;
  const configuredId = vars?.MEASUREMENT_ID || process.env.EXPO_PUBLIC_MEASUREMENT_ID;
  if (configuredId && configuredId.startsWith("G-") && configuredId !== "G-xxx") {
    return configuredId;
  }
  return DEFAULT_MEASUREMENT_ID;
}

/**
 * Initializes Google Analytics 4 for Web and Native platforms.
 * Injects gtag.js on Web, or initializes persistent identity on Native.
 * Defer initAnalytics() until the user explicitly accepts analytical telemetry.
 */
export async function initAnalytics(): Promise<void> {
  const consented = await hasUserConsentedToAnalytics();
  if (!consented) {
    console.info("[Analytics] Telemetry deferred: user has not consented to analytics.");
    return;
  }

  if (isInitialized) return;

  const measurementId = getMeasurementId();

  if (Platform.OS === "web" && typeof window !== "undefined" && typeof document !== "undefined") {
    try {
      window.dataLayer = window.dataLayer || [];
      if (!window.gtag) {
        window.gtag = function gtag() {
          window.dataLayer.push(arguments);
        };
      }

      // Check if script already loaded
      const existingScript = document.getElementById("ga-gtag-script");
      if (!existingScript) {
        const script = document.createElement("script");
        script.id = "ga-gtag-script";
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
        document.head.appendChild(script);
      }

      window.gtag("js", new Date());
      window.gtag("config", measurementId, {
        send_page_view: false, // We control page_view manually for SPA / Expo Router precision
      });

      console.info(`[Analytics] Web GA4 initialized with ${measurementId}`);
    } catch (err) {
      console.warn("[Analytics] Failed to initialize web gtag.js:", err);
    }
  } else {
    // Native React Native client initialization
    await getOrCreateClientId();
    console.info(`[Analytics] Native GA4 client initialized with ${measurementId}`);
  }

  isInitialized = true;
}

/**
 * Dispatches an event payload directly to Google Analytics via native HTTP or web gtag.
 * Defers/drops dispatch if user has not consented to analytical telemetry.
 * @param {string} eventName - Standardized event name (snake_case).
 * @param {IAnalyticsEventPayload} params - Event parameters.
 */
export async function logAnalyticsEvent(eventName: string, params: IAnalyticsEventPayload = {}): Promise<void> {
  if (!getAnalyticsConsentSync()) {
    const consented = await hasUserConsentedToAnalytics();
    if (!consented) {
      return;
    }
  }

  const measurementId = getMeasurementId();

  // Clean params: omit undefined
  const sanitizedParams: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      sanitizedParams[key] = value;
    }
  }

  // Include active screen and user role if available
  if (activeScreenName && sanitizedParams.screen_name === undefined) {
    sanitizedParams.screen_name = activeScreenName;
  }
  if (activeUserId && sanitizedParams.user_id === undefined) {
    sanitizedParams.user_id = activeUserId;
  }

  // Append user properties
  for (const [key, val] of Object.entries(activeUserProps)) {
    if (sanitizedParams[`up_${key}`] === undefined) {
      sanitizedParams[`up_${key}`] = val;
    }
  }

  if (Platform.OS === "web" && typeof window !== "undefined" && window.gtag) {
    try {
      window.gtag("event", eventName, sanitizedParams);
      console.debug(`[Analytics] (Web) ${eventName}:`, sanitizedParams);
      return;
    } catch (err) {
      console.warn(`[Analytics] Error dispatching event "${eventName}" via gtag:`, err);
    }
  }

  // Native / Fallback dispatch via Google Analytics collection endpoint
  try {
    const clientId = await getOrCreateClientId();
    const query = new URLSearchParams();
    query.set("v", "2");
    query.set("tid", measurementId);
    query.set("cid", clientId);
    query.set("en", eventName);
    if (activeUserId) {
      query.set("uid", activeUserId);
    }

    for (const [key, val] of Object.entries(sanitizedParams)) {
      query.set(`ep.${key}`, String(val));
    }

    const hitUrl = `https://www.google-analytics.com/g/collect?${query.toString()}`;

    // Non-blocking fetch
    fetch(hitUrl, { method: "POST", mode: "no-cors" }).catch((networkErr) => {
      console.debug(`[Analytics] Non-critical dispatch failure for ${eventName}:`, networkErr);
    });

    console.debug(`[Analytics] (Native/HTTP) ${eventName}:`, sanitizedParams);
  } catch (err) {
    console.debug(`[Analytics] Failed dispatching native hit for ${eventName}:`, err);
  }
}

/**
 * Sets the active authenticated user ID and associated user traits for all subsequent analytics telemetry.
 * @param {string} userId - User identifier (UID).
 * @param {Record<string, string | number | boolean>} properties - Additional user properties.
 */
export function setAnalyticsUser(
  userId: string,
  properties: Record<string, string | number | boolean> = {}
): void {
  activeUserId = userId;
  activeUserProps = {
    ...properties,
    platform: Platform.OS,
  };

  if (!getAnalyticsConsentSync()) {
    return;
  }

  const measurementId = getMeasurementId();

  if (Platform.OS === "web" && typeof window !== "undefined" && window.gtag) {
    try {
      window.gtag("set", "user_properties", activeUserProps);
      window.gtag("config", measurementId, {
        user_id: userId,
      });
    } catch (err) {
      console.warn("[Analytics] Error setting user identity via gtag:", err);
    }
  }

  logAnalyticsEvent("login", {
    method: String(properties.method || "credential"),
    user_id: userId,
  });
}

/**
 * Clears user identity and associated properties upon logout.
 */
export function clearAnalyticsUser(): void {
  if (getAnalyticsConsentSync()) {
    logAnalyticsEvent("logout", {
      user_id: activeUserId || "anonymous",
    });
  }

  activeUserId = null;
  activeUserProps = {};

  const measurementId = getMeasurementId();
  if (Platform.OS === "web" && typeof window !== "undefined" && window.gtag && getAnalyticsConsentSync()) {
    try {
      window.gtag("config", measurementId, {
        user_id: undefined,
      });
    } catch (err) {
      console.warn("[Analytics] Error clearing user identity via gtag:", err);
    }
  }
}

/**
 * Updates the currently active screen/route name for contextual event association.
 * @param {string} screenName - Screen identifier or route pathname.
 */
export function setActiveScreen(screenName: string): void {
  activeScreenName = screenName;
}

/**
 * Tracks a page visit.
 * @param {string} pagePath - Route or pathname.
 * @param {string} [pageTitle] - Optional page title.
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  setActiveScreen(pagePath);
  logAnalyticsEvent("page_view", {
    page_location: pagePath,
    page_path: pagePath,
    page_title: pageTitle || pagePath,
  });
}

/**
 * Tracks the dwell time spent on a page upon leaving.
 * @param {string} pagePath - Route or pathname.
 * @param {number} durationSeconds - Seconds spent on the page.
 */
export function trackPageDwellTime(pagePath: string, durationSeconds: number): void {
  if (durationSeconds <= 0) return;
  logAnalyticsEvent("page_dwell_time", {
    page_path: pagePath,
    duration_seconds: Math.round(durationSeconds),
    duration_ms: Math.round(durationSeconds * 1000),
  });
}

/**
 * Tracks a button click interaction.
 * @param {string} buttonName - Label or identifier of the button.
 * @param {string} [screen] - Optional screen context override.
 * @param {IAnalyticsEventPayload} [extra] - Additional context.
 */
export function trackButtonClick(buttonName: string, screen?: string, extra: IAnalyticsEventPayload = {}): void {
  logAnalyticsEvent("button_click", {
    button_name: buttonName,
    screen_name: screen || activeScreenName,
    ...extra,
  });
}

/**
 * Tracks an input field interaction upon blur or input change.
 * @param {string} inputName - Placeholder, field name, or identifier.
 * @param {string} [screen] - Optional screen context override.
 * @param {IAnalyticsEventPayload} [extra] - Additional context.
 */
export function trackInputUsed(inputName: string, screen?: string, extra: IAnalyticsEventPayload = {}): void {
  logAnalyticsEvent("input_used", {
    input_name: inputName,
    screen_name: screen || activeScreenName,
    ...extra,
  });
}

/**
 * Tracks media file uploads.
 * @param {string | number} mediaIdOrCount - ID or batch count.
 * @param {number} [count=1] - Number of media items.
 * @param {number} [totalBytes] - Size in bytes.
 * @param {string} [mediaType] - MIME type or category.
 */
export function trackMediaUploaded(
  mediaIdOrCount: string | number,
  count: number = 1,
  totalBytes?: number,
  mediaType?: string
): void {
  logAnalyticsEvent("media_uploaded", {
    media_count: count,
    media_id: typeof mediaIdOrCount === "string" ? mediaIdOrCount : undefined,
    size_bytes: totalBytes,
    media_type: mediaType,
  });
}

/**
 * Tracks direct high-resolution media captures (photo, video, audio).
 * @param {"photo" | "video" | "audio"} captureType - Media capture mode.
 * @param {string} [resolution] - Resolution tag or sample rate.
 * @param {boolean} [hasGps=false] - Whether GPS geotagging was captured.
 */
export function trackMediaCaptured(
  captureType: "photo" | "video" | "audio",
  resolution?: string,
  hasGps: boolean = false
): void {
  logAnalyticsEvent("media_captured", {
    capture_type: captureType,
    resolution: resolution || "high",
    has_gps: hasGps,
  });
}

/**
 * Tracks memory creation.
 * @param {string} memoryId - Memory identifier.
 * @param {number} [mediaCount=0] - Number of associated media items.
 * @param {number} [titleLength] - Character length of memory title.
 */
export function trackMemoryAdded(memoryId: string, mediaCount: number = 0, titleLength?: number): void {
  logAnalyticsEvent("memory_added", {
    memory_id: memoryId,
    media_count: mediaCount,
    title_length: titleLength,
  });
}

/**
 * Tracks contact addition.
 * @param {string} contactId - Contact identifier.
 * @param {boolean} hasEmail - Whether an email address was provided.
 */
export function trackContactAdded(contactId: string, hasEmail: boolean): void {
  logAnalyticsEvent("contact_added", {
    contact_id: contactId,
    has_email: hasEmail,
  });
}

/**
 * Tracks group creation.
 * @param {string} groupId - Group identifier.
 * @param {string} [groupName] - Optional group name.
 */
export function trackGroupAdded(groupId: string, groupName?: string): void {
  logAnalyticsEvent("group_added", {
    group_id: groupId,
    group_name: groupName,
  });
}

/**
 * Tracks share creation.
 * @param {string} shareId - Share identifier.
 * @param {string} itemType - Type of item shared (memory, media).
 * @param {string} [targetType] - Target destination (contact, group, public).
 */
export function trackShareAdded(shareId: string, itemType: string, targetType?: string): void {
  logAnalyticsEvent("share_added", {
    share_id: shareId,
    item_type: itemType,
    target_type: targetType,
  });
}

/**
 * Tracks message transmission.
 * @param {string} conversationId - Conversation or group identifier.
 * @param {string} [messageType="text"] - Type of message.
 */
export function trackMessageAdded(conversationId: string, messageType: string = "text"): void {
  logAnalyticsEvent("message_added", {
    conversation_id: conversationId,
    message_type: messageType,
  });
}

/**
 * Tracks custom or milestone domain events.
 * @param {string} eventId - Unique event identifier.
 * @param {string} eventType - Category or name of event.
 * @param {IAnalyticsEventPayload} [extra] - Additional attributes.
 */
export function trackEventAdded(eventId: string, eventType: string, extra: IAnalyticsEventPayload = {}): void {
  logAnalyticsEvent("event_added", {
    event_id: eventId,
    event_type: eventType,
    ...extra,
  });
}

/**
 * Tracks device and client performance metrics (memory, network, CPU).
 * @param {IPerformanceMetrics} metrics - Telemetry payload.
 */
export function trackPerformanceMetrics(metrics: IPerformanceMetrics): void {
  logAnalyticsEvent("device_performance", {
    memory_used_mb: metrics.memoryUsageBytes ? Math.round(metrics.memoryUsageBytes / (1024 * 1024)) : undefined,
    memory_total_mb: metrics.memoryTotalBytes ? Math.round(metrics.memoryTotalBytes / (1024 * 1024)) : undefined,
    memory_heap_ratio: metrics.memoryHeapRatio ? Number(metrics.memoryHeapRatio.toFixed(3)) : undefined,
    network_type: metrics.networkEffectiveType,
    network_downlink_mbps: metrics.networkDownlinkMbps,
    network_rtt_ms: metrics.networkRttMs,
    api_latency_ms: metrics.apiLatencyMs ? Math.round(metrics.apiLatencyMs) : undefined,
    api_function: metrics.apiFunction,
    cpu_lag_ms: metrics.cpuLagMs ? Math.round(metrics.cpuLagMs) : undefined,
    long_task_count: metrics.longTaskCount,
  });
}
